import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  useTheme,
  alpha,
} from '@mui/material';
import { formatYAxisTick } from '../../utils/chartUtils';
import type { ScatterDataPoint } from './ScatterPlotChart';

/** Calcula regresión lineal y R² (equivalente a Excel) */
function linearRegression(points: { x: number; y: number }[]): { slope: number; intercept: number; r2: number } {
  const n = points.length;
  if (n < 2) return { slope: 0, intercept: 0, r2: 0 };
  const sumX = points.reduce((s, p) => s + p.x, 0);
  const sumY = points.reduce((s, p) => s + p.y, 0);
  const sumXX = points.reduce((s, p) => s + p.x * p.x, 0);
  const sumXY = points.reduce((s, p) => s + p.x * p.y, 0);
  const xMean = sumX / n;
  const yMean = sumY / n;
  const ssXX = sumXX - n * xMean * xMean;
  const ssXY = sumXY - n * xMean * yMean;
  const slope = ssXX !== 0 ? ssXY / ssXX : 0;
  const intercept = yMean - slope * xMean;
  const ssTot = points.reduce((s, p) => s + (p.y - yMean) ** 2, 0);
  const ssRes = points.reduce((s, p) => s + (p.y - (intercept + slope * p.x)) ** 2, 0);
  const r2 = ssTot > 0 ? 1 - ssRes / ssTot : 0;
  return { slope, intercept, r2 };
}

export interface CanvasScatterPlotProps {
  data: ScatterDataPoint[];
  xLabel: string;
  yLabel: string;
  primaryColor?: string;
  height?: number;
  isLoading?: boolean;
  emptyMessage?: string;
  showRegression?: boolean;
}

export const CanvasScatterPlot: React.FC<CanvasScatterPlotProps> = ({
  data,
  xLabel,
  yLabel,
  primaryColor,
  height = 600,
  isLoading = false,
  emptyMessage = 'No hay datos disponibles para el scatter plot.',
  showRegression = false,
}) => {
  const theme = useTheme();
  const color = primaryColor || theme.palette.primary.main;
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(800);

  const validData = useMemo(
    () =>
      data.filter(
        (d) =>
          d.x !== null &&
          d.x !== undefined &&
          isFinite(d.x) &&
          d.y !== null &&
          d.y !== undefined &&
          isFinite(d.y)
      ),
    [data]
  );

  const regression = useMemo(() => {
    if (validData.length < 2 || !showRegression) return null;
    return linearRegression(validData.map((p) => ({ x: p.x, y: p.y })));
  }, [validData, showRegression]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (w && w > 0) setWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const w = width;
    const h = height;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    canvas.style.width = `${w}px`;
    canvas.style.height = `${h}px`;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);

    const pad = { top: 24, right: 24, bottom: 48, left: 64 };
    const plotW = w - pad.left - pad.right;
    const plotH = h - pad.top - pad.bottom;

    if (validData.length === 0) return;

    const xs = validData.map((d) => d.x);
    const ys = validData.map((d) => d.y);
    let xMin = Math.min(...xs);
    let xMax = Math.max(...xs);
    let yMin = Math.min(...ys);
    let yMax = Math.max(...ys);
    const xPad = (xMax - xMin || 1) * 0.05;
    const yPad = (yMax - yMin || 1) * 0.05;
    xMin -= xPad;
    xMax += xPad;
    yMin -= yPad;
    yMax += yPad;

    const toX = (x: number) => pad.left + ((x - xMin) / (xMax - xMin)) * plotW;
    const toY = (y: number) => pad.top + plotH - ((y - yMin) / (yMax - yMin)) * plotH;

    ctx.strokeStyle = theme.palette.divider;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(pad.left, pad.top);
    ctx.lineTo(pad.left, pad.top + plotH);
    ctx.lineTo(pad.left + plotW, pad.top + plotH);
    ctx.stroke();

    ctx.fillStyle = color;
    const radius = validData.length > 1500 ? 1.5 : 2.5;
    for (const p of validData) {
      ctx.beginPath();
      ctx.arc(toX(p.x), toY(p.y), radius, 0, Math.PI * 2);
      ctx.fill();
    }

    if (regression) {
      const y0 = regression.intercept + regression.slope * xMin;
      const y1 = regression.intercept + regression.slope * xMax;
      ctx.strokeStyle = theme.palette.secondary.main;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(toX(xMin), toY(y0));
      ctx.lineTo(toX(xMax), toY(y1));
      ctx.stroke();
    }

    ctx.fillStyle = theme.palette.text.secondary;
    ctx.font = '12px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(xLabel, pad.left + plotW / 2, h - 12);
    ctx.save();
    ctx.translate(16, pad.top + plotH / 2);
    ctx.rotate(-Math.PI / 2);
    ctx.fillText(yLabel, 0, 0);
    ctx.restore();

    ctx.textAlign = 'right';
    ctx.fillText(formatYAxisTick(yMin, yMin, yMax), pad.left - 8, toY(yMin) + 4);
    ctx.fillText(formatYAxisTick(yMax, yMin, yMax), pad.left - 8, toY(yMax) + 4);
    ctx.textAlign = 'center';
    ctx.fillText(formatYAxisTick(xMin, xMin, xMax), toX(xMin), pad.top + plotH + 20);
    ctx.fillText(formatYAxisTick(xMax, xMin, xMax), toX(xMax), pad.top + plotH + 20);
  }, [validData, regression, width, height, color, theme, xLabel, yLabel]);

  useEffect(() => {
    draw();
  }, [draw]);

  const formatCoeff = (n: number) =>
    Math.abs(n) < 0.001 || Math.abs(n) > 1e6 ? n.toExponential(2) : n.toFixed(4);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height }}>
        <CircularProgress />
      </Box>
    );
  }

  if (validData.length === 0) {
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        {emptyMessage}
      </Alert>
    );
  }

  return (
    <Box ref={containerRef} sx={{ width: '100%' }}>
      {regression && (
        <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
          {`y = ${formatCoeff(regression.slope)}·x ${regression.intercept >= 0 ? '+' : ''} ${formatCoeff(regression.intercept)}`}
          {' · '}
          {`R² = ${regression.r2.toFixed(4)}`}
        </Typography>
      )}
      <Box
        sx={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
          bgcolor: alpha(theme.palette.background.paper, 0.6),
        }}
      >
        <canvas ref={canvasRef} role="img" aria-label={`Scatter ${xLabel} vs ${yLabel}`} />
      </Box>
    </Box>
  );
};
